<template>
  <v-col
    cols="12"
    :sm="9"
    :md="isFullWidth ? 12 : 6"
    :lg="isFullWidth ? 12 : 6"
    :xl="isFullWidth ? 12 : 4"
    :xxl="isFullWidth ? 12 : 3"
    :class="{ 'mx-auto': sm, 'pa-0': borderless }"
  >
    <v-card
      class="d-flex flex-column lsm-widget"
      :class="{ 'lsm-widget-height': !isFullWidth }"
      :variant="borderless ? 'flat' : undefined"
      :image="bgImage"
    >
      <v-card-title
        v-if="title || $slots.title"
        class="d-flex flex-column justify-center text-headline-large text-center text-wrap bg-primary-lighten-1 lsm-widget-height text-primary-darken-1"
        data-testid="title"
      >
        <slot name="title">
          {{ title }}
        </slot>
        <div
          v-if="icon"
          class="ma-6"
        >
          <v-icon
            size="x-large"
            color="primary"
            class="fas"
            :class="icon"
          />
        </div>
      </v-card-title>
      <v-card-title
        v-if="$slots.warning"
        class="d-flex flex-column justify-center text-headline-large text-center text-wrap bg-primary-lighten-1 bg-warning"
        data-testid="warning"
      >
        <slot
          name="warning"
        >
          {{ $slots.warning() }}
        </slot>
      </v-card-title>
      <v-img
        v-if="image"
        cover
        :src="image"
        class="lsm-widget-height"
      />
      <v-card-subtitle
        v-if="subtitle"
        data-testid="subtitle"
        class="text-headline-small mt-4 text-center"
      >
        {{ subtitle }}
      </v-card-subtitle>
      <v-card-text
        class="text-body-large text-center"
        :class="textColor ? `text-${textColor}` : ''"
        data-testid="text"
      >
        <slot>
          {{ text }}
        </slot>
      </v-card-text>
      <v-spacer />
      <template v-if="$slots.buttons">
        <v-card-actions>
          <v-row
            class="d-flex"
            density="compact"
          >
            <template
              v-for="(button, index) in $slots.buttons()"
              :key="index"
            >
              <v-col
                v-if="button?.props"
                class="flex-grow"
              >
                <v-btn
                  :elevation="2"
                  color="secondary"
                  size="large"
                  variant="flat"
                  block
                  v-bind="button?.props"
                >
                  <template
                    v-if="!button.children"
                    #default
                  >
                    {{ button.props?.text }}
                  </template>
                  <template
                    v-for="slot in Object.keys(button?.children ?? {})"
                    :key="slot"
                    #[slot]
                  >
                    <component
                      :is="button.children[slot]"
                    />
                  </template>
                </v-btn>
              </v-col>
            </template>
          </v-row>
        </v-card-actions>
      </template>
    </v-card>
  </v-col>
</template>

<script setup lang="ts">
import type { VNode } from 'vue';
import { computed } from 'vue';
import { useDisplay } from 'vuetify';

const props = defineProps<{
  width?: WidgetWidth;
  borderless?: boolean;
  title?: string;
  subtitle?: string;
  text?: string;
  bgImage?: string;
  image?: string;
  icon?: string;
  textColor?: string;
}>();
defineSlots<{ buttons: () => VNode[]; title: () => VNode[]; warning: () => VNode[] }>();
const { sm } = useDisplay();
export type WidgetWidth = 'full';

const isFullWidth = computed(() => props.width === 'full');
</script>

<style scoped>
.lsm-widget {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
}

.lsm-widget-height {
    min-height: 200px;
}

.warning {
    min-height: 80px;
}
</style>
